/**
 * THE BOOTH STILL — who is on screen when there is no picture of the racing.
 *
 * Howard, 17 Aug 2026: *"here are the commentators for the booth to use as
 * placeholders whenever there is no action. The one facing the camera is for
 * yellow or red flags while the other one is while the race is going on."*
 *
 * That is a real broadcast convention and it is worth stating why it is the
 * right rule rather than just implementing it. A director cuts to the booth
 * WHEN THERE IS NOTHING ON TRACK TO CUT TO — under caution, before the green,
 * after the checkered. And the shot he takes is different in each case:
 *
 *   • Nothing is happening on track → they are talking TO THE VIEWER, so the
 *     camera is on their faces. That is Howard's "facing the camera" shot.
 *   • The race is running → they are watching it, and the shot over their
 *     shoulders has the track in it. They are looking at the racing, not at us.
 *   • The field is in the pits → the camera is on PIT ROAD, with the reporter
 *     who is standing there. Added 17 Aug when Howard sent a third still; it
 *     outranks both of the above, because it is the most specific thing known
 *     about where the cameras would actually be.
 *
 * So the rule is one question — **is the field actually racing?** — and it
 * generalises to the two flags Howard did not name without inventing anything:
 * pre-race and the checkered are both "not racing", and both are moments a real
 * broadcast spends looking at the booth.
 *
 * RED FLAG. Howard named one; RWBR's flag stand has no red. `Flag` is
 * `pre-race | green | yellow | white | checkered`, and the base rulebook stops a
 * race with a caution, never a red. Rather than invent a flag the rules do not
 * have (D-EZ-1), the yellow carries the case he meant — the field is circulating
 * behind the pace car and the booth is filling. If a red is ever added to the
 * engine it lands in `ON_CAMERA` and nothing else has to change.
 */

import type { Flag } from '../../components/boardModel.ts';
import { CAST } from './cast.ts';

export const CITE_STILL =
  'Howard, 17 Aug 2026 — "The one facing the camera is for yellow or red flags while the other one is while the race is going on."';

/** Which shot is up. */
export type BoothShot = 'on-camera' | 'on-track' | 'pit-road';

/**
 * Flags where the field is NOT racing, so the director is on the booth's faces.
 * Listed rather than derived, because a list is readable and this one is short.
 */
const ON_CAMERA: readonly Flag[] = ['pre-race', 'yellow', 'checkered'];

/**
 * Which shot the flag calls for.
 *
 * `white` is the last scheduled turn and is very much still racing — arguably
 * the most racing of the afternoon — so it takes the track shot with `green`.
 */
export function shotFor(flag: Flag): BoothShot {
  return ON_CAMERA.includes(flag) ? 'on-camera' : 'on-track';
}

/**
 * Where the two files live, and what they are.
 *
 * Served from `public/`, NOT from the art library. This is furniture: it is the
 * same two frames every race, it has no scene tag, no rarity and no track
 * category, and putting it in the library would mean it competed with actual
 * race photographs in the selector and counted against `maxPerSeason`. The
 * library is for pictures OF the racing.
 *
 * Missing files are not an error. `BoothStill` renders nothing at all if the
 * image will not load, and the panel simply stays as it was — no broken-image
 * icon, no empty frame with a caption under it.
 */
export const BOOTH_STILLS: Record<BoothShot, { src: string; alt: string; caption: string }> = {
  'on-camera': {
    src: '/booth/on-camera.jpg',
    alt: `${CAST.host.name} and ${CAST.analyst.name} in the broadcast booth, facing the camera`,
    caption: 'In the booth',
  },
  'on-track': {
    src: '/booth/on-track.jpg',
    alt: `${CAST.host.name} and ${CAST.analyst.name} in the booth, watching the race`,
    caption: 'Watching it unfold',
  },
  'pit-road': {
    src: '/booth/pit-road.jpg',
    alt: `${CAST.pit.name} on pit road`,
    caption: `${CAST.pit.name}, on pit road`,
  },
};

/** What the shot depends on, beyond the flag. */
export interface StillContext {
  /**
   * Is the turn on screen a scheduled PIT turn (RB/xi)?
   *
   * This is the most specific fact available about what the cameras would be
   * looking at, so it outranks the flag — a broadcast cuts to pit road for a
   * round of stops, and it does that under caution most of all, which is when
   * most stops happen. That is a refinement of Howard's rule rather than a
   * contradiction of it: he set the booth pair's two shots before there was a
   * third picture to cut to.
   */
  onPitRoad?: boolean;
}

/**
 * Which still is up, in precedence order.
 *
 *   1. PIT ROAD — the field is in the pits, so that is where the camera is.
 *   2. ON CAMERA — the field is not racing, so they are talking to the viewer.
 *   3. ON TRACK — the field is racing, so they are watching it.
 */
export function stillFor(
  flag: Flag,
  context: StillContext = {},
): { src: string; alt: string; caption: string; shot: BoothShot } {
  const shot: BoothShot = context.onPitRoad ? 'pit-road' : shotFor(flag);
  return { ...BOOTH_STILLS[shot], shot };
}
